import type {ReactElement, ReactNode} from 'react';
import React, {useMemo, useState} from 'react';
import type {Active} from '@dnd-kit/core';
import {closestCenter, DndContext, DragOverlay, MouseSensor, TouchSensor, useSensor, useSensors} from '@dnd-kit/core';
import {rectSortingStrategy, SortableContext} from '@dnd-kit/sortable';
import type {MobilePageSelectorProps} from './mobile-page-selector.types';
import styles from './mobile-page-selector.module.scss';
import {useAppSelector} from '@/hooks';
import {MobilePageSelectorPage} from '@Components/poster-editor/components/mobile-page-selector-page/mobile-page-selector-page';
import {addNewPage} from '@Components/poster-editor/library/poster-editor-page-library';
import {MobilePageSelectionModeState} from '@Components/poster-editor/poster-editor.types';
import {useGetCurrentPageZoom} from '@Hooks/poster-editor/useGetCurrentPageZoom';
import {ZOOM_MIN} from '@PosterWhiteboard/poster/poster-scaling.class';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';

export function MobilePageSelector({className = ''}: MobilePageSelectorProps): ReactNode {
  const [activeId, setActiveId] = useState<Active | null>(null);

  const orderedPageIds = useAppSelector((state) => {
    return state.posterEditor?.posterObject?.pages.pageOrder;
  });
  const currentPageId = useAppSelector((state) => {
    return state.posterEditor.currentPageId;
  });
  const mobilePageSelectionModeState = useAppSelector((state) => {
    return state.posterEditor.mobilePageSelectionModeState;
  });
  const currentPageZoom = useGetCurrentPageZoom();

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 5,
      },
    })
  );

  const activeItem = useMemo(() => {
    if (!orderedPageIds || !activeId) {
      return null;
    }

    return orderedPageIds.find((id) => id === activeId.id);
  }, [activeId, orderedPageIds]);

  const handleDragEnd = ({active, over}: {active: Active; over: {id: string | number} | null}): void => {
    setActiveId(null);

    if (!over || !orderedPageIds) {
      return;
    }

    if (active.id === over.id) {
      return;
    }

    const newIndex = orderedPageIds.findIndex((id) => id === over.id);
    if (newIndex !== -1) {
      window.posterEditor?.whiteboard?.pages.movePageToIndex(String(active.id), newIndex);
    }
  };

  const getPages = (): ReactElement[] => {
    if (!orderedPageIds) {
      return [];
    }

    return orderedPageIds.map((pageHashedId, index) => {
      return <MobilePageSelectorPage key={pageHashedId} pageHashedId={pageHashedId} pageNumber={index + 1} isSelected={currentPageId === pageHashedId} />;
    });
  };

  const getDragOverlayContent = (): ReactElement | null => {
    if (!activeItem || !orderedPageIds) {
      return null;
    }

    const index = orderedPageIds.indexOf(activeItem);
    return <MobilePageSelectorPage pageHashedId={activeItem} pageNumber={index + 1} isSelected={true} isDragOverlay />;
  };

  const getAddPageButton = (): ReactNode => {
    return (
      <div className={'spacing-p-1'}>
        <button
          type="button"
          aria-label={window.i18next.t('pmwjs_add_page')}
          className={`_full-width flexbox flex-center _cursor-pointer radius-8 ${styles.addPageButton}`}
          onClick={(): void => {
            void addNewPage();
          }}
        >
          <Icon icon="icon-plus" size={IconSize.SIZE_ICON_32} className={styles.addPageIcon} />
        </button>
      </div>
    );
  };

  const getOpacityForPageSelectionModeUi = (): number => {
    if (mobilePageSelectionModeState === MobilePageSelectionModeState.ACTIVE) {
      return 1;
    }

    return getOpacityBasedOnZoomValue();
  };

  const getOpacityBasedOnZoomValue = (): number => {
    const zoom = currentPageZoom ?? 1;
    const fitToScreenScale = window.posterEditor?.whiteboard?.scaling.getFitToScreenScale() ?? 1;
    const minZoom = window.posterEditor?.whiteboard?.scaling.getMinZoom() ?? ZOOM_MIN;

    const zoomRatio = normalizeZoomToRange(zoom, minZoom, fitToScreenScale);
    const invertedRatio = 1 - zoomRatio;

    return applyEaseInCurve(invertedRatio);
  };

  const normalizeZoomToRange = (zoom: number, minZoom: number, maxZoom: number): number => {
    if (maxZoom === minZoom) {
      return 0;
    }

    return (zoom - minZoom) / (maxZoom - minZoom);
  };

  const applyEaseInCurve = (value: number): number => {
    const EASE_IN_EXPONENT = 0.5;
    return parseFloat(Math.pow(value, EASE_IN_EXPONENT).toFixed(2));
  };

  if (mobilePageSelectionModeState === MobilePageSelectionModeState.INACTIVE) {
    return null;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({active}): void => {
        setActiveId(active);
        if (navigator.vibrate) {
          navigator.vibrate(100);
        }
        if (active.id !== currentPageId) {
          window.posterEditor?.whiteboard?.setCurrentPage(String(active.id));
        }
      }}
      onDragEnd={handleDragEnd}
      onDragCancel={(): void => {
        setActiveId(null);
      }}
    >
      <SortableContext items={orderedPageIds ?? []} strategy={rectSortingStrategy}>
        <div
          style={{
            opacity: `${getOpacityForPageSelectionModeUi()}`,
          }}
          className={`${className} ${styles.pagesContainer} spacing-p-4 _full-height`}
        >
          {getPages()}
          {getAddPageButton()}
        </div>
      </SortableContext>

      <DragOverlay>{getDragOverlayContent()}</DragOverlay>
    </DndContext>
  );
}
